import { FC, useMemo, useState } from 'react'

import qs from 'qs'
import { styled } from 'styled-components'

import {
  Form,
  Input,
  Panels,
  Bar,
  Modal,
  ModelList,
  LoadingContainer,
  Select,
  CountryCodeSelect,
  StateCodeSelect,
  DateInput,
  Pagination,
} from 'peach/components'
import { useParams } from 'peach/router'

import { Page, Switch } from 'core/components'
import { R } from 'core/helpers'
import { useQueryString } from 'core/hooks'
import {
  useInternalBorrowersList,
  useInternalBorrowerCreate,
  PickRequired,
} from 'core/types'
import { useInternalBorrowerSearchPaged } from 'core/types/hooks/pagedHooks'
import {
  InternalBorrowersListVariables,
  InternalBorrowerSearchVariables,
} from 'core/types/operations'
import BorrowerSearch from './BorrowerSearch'

const PeopleList: FC = () => {
  const { companyId } = useParams<{ companyId?: string }>()

  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isExcludeInactive, setExcludeInactive] = useState(true)
  const [query, setQuery] = useQueryString<
    PickRequired<
      NonNullable<InternalBorrowersListVariables['query']>,
      'limit' | 'startingAfter' | 'endingBefore'
    >
  >({
    limit: 50,
    startingAfter: '',
    endingBefore: '',
  })

  const listApiQuery = useMemo((): NonNullable<
    InternalBorrowersListVariables['query']
  > => {
    if (isExcludeInactive) {
      return { ...query, status: 'active' }
    }
    const { status: _status, ...rest } = query as typeof query & {
      status?: 'active' | 'inactive'
    }
    return rest
  }, [query, isExcludeInactive])

  const searchQueryParams = useMemo(() => {
    if (!searchQuery) return {}
    const parsed = qs.parse(searchQuery)
    return parsed
  }, [searchQuery])

  const hasSearchQuery = Object.keys(searchQueryParams).length > 0

  const searchApiQuery = useMemo((): NonNullable<
    InternalBorrowerSearchVariables['query']
  > => {
    const { status: _searchStatusOmitted, ...rest } = {
      ...searchQueryParams,
    } as Record<string, unknown>
    return rest as NonNullable<InternalBorrowerSearchVariables['query']>
  }, [searchQueryParams])

  const listResult = useInternalBorrowersList({
    companyId,
    query: listApiQuery,
    options: { enabled: !hasSearchQuery },
  })

  const searchResult = useInternalBorrowerSearchPaged({
    companyId,
    query: searchApiQuery,
    options: { enabled: hasSearchQuery },
  })

  const { mutateAsync: createPerson } = useInternalBorrowerCreate({ companyId })

  const {
    data: rawData,
    pagination,
    isLoading,
    isFetching,
    isError,
  } = hasSearchQuery ? searchResult : listResult

  const data = !isExcludeInactive
    ? rawData
    : !rawData
    ? undefined
    : R.filter(rawData, (person) => person.status === 'active')

  const { total: count, nextUrl, previousUrl } = pagination ?? {}

  const nextBorrower = qs.parse(nextUrl ?? '')?.startingAfter
  const previousBorrower = qs.parse(previousUrl ?? '')?.endingBefore

  const createBorrower = (
    <Modal
      initialValue={{
        status: 'active',
        identity: {
          identityType: 'SSN',
          value: '999999999',
        },
      }}
      onSubmit={createPerson}
      title='Create Borrower'
      trigger
    >
      <Input formKey='externalId' />
      <Select formKey='borrowerType' options={['person', 'business']} />
      <Select formKey='status' options={['active', 'inactive']} />

      <Form.If if={{ borrowerType: 'person' }}>
        <Form.Provider formKey='name' section>
          <Input formKey='firstName' />
          <Input formKey='lastName' />
        </Form.Provider>
        <DateInput formKey='dateOfBirth' />
      </Form.If>

      <Form.If if={{ borrowerType: 'business' }}>
        <Form.Provider formKey='businessDetails' section>
          <Select
            formKey='businessType'
            options={[
              'LLC',
              'cCorporation',
              'sCorporation',
              'generalPartnership',
              'limitedPartnership',
              'soleProprietor',
              'nonProfit',
              'other',
            ]}
          />
          <Input formKey='businessName' />
          <Input formKey='businessLegalName' />
          <CountryCodeSelect formKey='incorporatedCountry' />
          <StateCodeSelect formKey='incorporatedState' />
        </Form.Provider>
      </Form.If>

      <Form.Provider formKey='identity' section>
        <Input formKey='identityType' />
        <Input formKey='value' />
      </Form.Provider>
    </Modal>
  )

  return (
    <Panels
      header={
        <Bar
          fullWidth
          left={
            <>
              <BorrowerSearch
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
              <Pagination
                limit={query.limit ?? 0}
                onNext={() => {
                  setQuery({
                    ...query,
                    startingAfter: nextBorrower,
                    endingBefore: '',
                  })
                  setPage(page + 1)
                }}
                onPrevious={() => {
                  setQuery({
                    ...query,
                    startingAfter: '',
                    endingBefore: page > 2 ? previousBorrower : '',
                  })
                  setPage(page - 1)
                }}
                page={page}
                total={
                  hasSearchQuery &&
                  isExcludeInactive &&
                  rawData &&
                  !isFetching &&
                  rawData.length !== (data?.length ?? 0)
                    ? undefined
                    : count
                }
                variant='compact'
              />
              {createBorrower}
            </>
          }
        />
      }
    >
      <LoadingContainer error={isError} loading={isLoading || isFetching}>
        <Page variant='full'>
          <SwitchContainer>
            <Switch
              checked={isExcludeInactive}
              id='people-exclude-inactive'
              label='Exclude Inactive'
              name='excludeInactive'
              onCheckedChange={(checked) => {
                setExcludeInactive(checked)
                setPage(1)
                setQuery({
                  ...query,
                  startingAfter: '',
                  endingBefore: '',
                })
              }}
            />
          </SwitchContainer>
          <ModelList json={data ?? []} title='Borrowers' />
        </Page>
      </LoadingContainer>
    </Panels>
  )
}

export default PeopleList

const SwitchContainer = styled.div`
  margin-bottom: 12px;
`
